import type * as models from 'pkg/api/models';
import { getOrganizationId } from 'pkg/api/models/group/utils';
import type { OrganizationsLocalStorageData } from 'pkg/identity/account';
import * as localStorageHelpers from 'pkg/local_storage';

export const setDrawerVisibility = (visibleDrawer: boolean) => {
	if (visibleDrawer) {
		document.body.classList.remove('no-drawer');
	} else {
		document.body.classList.add('no-drawer');
	}
};

export const setTabBarVisibility = (tabBarVisible: boolean) => {
	if (tabBarVisible) {
		document.body.classList.remove('hide-tab-bar');
	} else {
		document.body.classList.add('hide-tab-bar');
	}
};

/**
 * Sets up all provided orgs in the local storage with default states for lastView
 * and activeMembership. This checks if a user has been removed from the org as well and clears that key value pair from the storage.
 *
 * @param organizations orgs to include in the local storage setup
 */

export function setupOrganizationLocalStorage(
	organizations: models.group.OrganizationData[]
) {
	const organizationsLocalStorage =
		localStorageHelpers.getJsonItem<OrganizationsLocalStorageData>(
			localStorageHelpers.LocalStorageKeys.Organizations
		) || {};
	const providedOrgIds = organizations.map((o) => o.id);
	const localStorageOrgIds = Object.keys(organizationsLocalStorage).map((v) =>
		Number.parseInt(v, 10)
	);

	// Migrate old activeMembership data to be set if it exist
	const storedMembership =
		localStorageHelpers.getJsonItem<models.membership.Membership>(
			localStorageHelpers.LocalStorageKeys.ActiveMembership
		) || null;
	let storedMembershipOrgId = 0;

	if (storedMembership?.group) {
		storedMembershipOrgId = getOrganizationId(
			storedMembership.group
		);
	}

	organizations.forEach(async (org) => {
		if (!organizationsLocalStorage[org.id]) {
			organizationsLocalStorage[org.id] = {
				activeMembership: null,
				orgToken: '',
			};

			// If we're in the org that's stored on the membership we set that orgs
			// activeMembership to the stored membership and active organization
			// to the orgs ID
			if (storedMembershipOrgId && org.id === storedMembershipOrgId) {
				organizationsLocalStorage[org.id].activeMembership = storedMembership;

				localStorageHelpers.setItem(
					localStorageHelpers.LocalStorageKeys.ActiveOrganizationId,
					storedMembershipOrgId.toString()
				);
			}
		}
	});

	// At this point it's safe to remove the old activeMembership item
	localStorageHelpers.removeItem(
		localStorageHelpers.LocalStorageKeys.ActiveMembership
	);

	localStorageOrgIds.forEach((id) => {
		// If there's a local storage id that doesn't exist in the provided
		// we've been removed from that org
		if (!providedOrgIds.includes(id)) {
			delete organizationsLocalStorage[id];
		}
	});

	localStorageHelpers.setJsonItem(
		localStorageHelpers.LocalStorageKeys.Organizations,
		organizationsLocalStorage
	);
}
